import React, { useState, useRef, useEffect, useId } from 'react'
import { Sparkle, Eye, ArrowsClockwise, Download, CaretRight, CaretDown, StackSimple, UploadSimple, X, Palette, Image, ChatText, Paperclip, MagnifyingGlass, Check, Play } from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { Field } from '@/components/ui/Field'
import { toast } from 'sonner'
import { presentationsApi, materialsApi, authApi } from '@/lib/api'
import MaterialSelector from './MaterialSelector'
import ContactFieldSelector from './ContactFieldSelector'
import { FileText } from '@phosphor-icons/react'
import type { WorkflowNodeData, DeckContentFocus, DeckColorTheme, DeckOutputFormat, EmailTone, ContactContextField, CollateralMode } from '@/types/workflow'

interface GenerateDeckConfigProps {
  data: WorkflowNodeData
  updateField: (field: string, value: any) => void
  upstreamEnrichmentFields?: ContactContextField[]
}

interface SlidePreview {
  type: string
  title?: string
  subtitle?: string
  icon?: string
  bullets?: (string | { text: string; icon?: string })[]
  left_header?: string
  right_header?: string
  left_bullets?: (string | { text: string; icon?: string })[]
  right_bullets?: (string | { text: string; icon?: string })[]
  stats?: { value: string; label: string; icon?: string }[]
  quote?: string
  attribution?: string
  cta?: string
}

interface DiscussionEntry {
  role: string
  content: string
}

interface PreviewData {
  title: string
  slides: SlidePreview[]
  color_theme?: Record<string, string>
  discussion?: DiscussionEntry[]
  contact_name?: string
}

// Option tables live in @/lib/deckOptions — shared with the Materials page
// "Generate with AI" modal so the two surfaces can never drift apart.
import { contentFocusOptions, deckToneOptions as tones, deckColorThemes as colorThemes } from '@/lib/deckOptions'

const slideTypeLabels: Record<string, { label: string; color: string }> = {
  title: { label: 'Title', color: 'bg-blue-500/15 text-blue-600 dark:text-blue-400' },
  section: { label: 'Section', color: 'bg-purple-500/15 text-purple-600 dark:text-purple-400' },
  content: { label: 'Content', color: 'bg-green-500/15 text-green-600 dark:text-green-400' },
  two_column: { label: '2-Column', color: 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-400' },
  stats: { label: 'Stats', color: 'bg-amber-500/15 text-amber-600 dark:text-amber-400' },
  quote: { label: 'Quote', color: 'bg-pink-500/15 text-pink-600 dark:text-pink-400' },
  closing: { label: 'Closing', color: 'bg-orange-500/15 text-orange-600 dark:text-orange-400' },
}

const agentIcons: Record<string, { emoji: string; color: string }> = {
  'Content Strategist': { emoji: '🧭', color: 'border-blue-400 bg-blue-500/15' },
  'Copywriter': { emoji: '✍️', color: 'border-green-400 bg-green-500/15' },
  'Design Director': { emoji: '🎨', color: 'border-purple-400 bg-purple-500/15' },
}

import { cn } from '@/lib/utils'
import {
  inputClass,
  inputStyle,
  sectionClass,
  textareaClass,
  infoBannerClass,
  successBannerClass,
  dangerBannerClass,
  sectionEyebrowStyle,
} from './_kit'
import { LockSimple } from '@phosphor-icons/react'
import { usePlanFeatures } from '@/hooks/usePlanFeatures'
function getBulletText(b: string | { text: string; icon?: string }): { text: string; icon?: string } {
  if (typeof b === 'string') return { text: b }
  return b
}

function extractPresentationError(err: any, fallback: string): string {
  const isTimeout = err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '')
  if (isTimeout) {
    return 'The request timed out. The deck pipeline is slow right now — please try again.'
  }
  const detail = err?.response?.data?.detail
  if (typeof detail === 'string' && detail) return detail
  if (err?.message) return err.message
  return fallback
}

const SlideCard: React.FC<{ slide: SlidePreview; index: number }> = ({ slide, index }) => {
  const [isOpen, setIsOpen] = useState(false)
  const typeInfo = slideTypeLabels[slide.type] || slideTypeLabels.content

  return (
    <div className="border border-border/50 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted transition-all duration-150"
      >
        <span className="text-xs font-mono text-muted-foreground w-5">{index + 1}</span>
        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-emphasis ${typeInfo.color}`}>
          {typeInfo.label}
        </span>
        {slide.icon && <span className="text-sm">{slide.icon}</span>}
        <span className="text-sm text-foreground truncate flex-1">
          {slide.title || slide.quote?.slice(0, 40) || 'Untitled'}
        </span>
        <CaretRight className={`w-3 h-3 text-muted-foreground transition-transform ${isOpen ? 'rotate-90' : ''}`} />
      </button>
      {isOpen && (
        <div className="px-3 pb-3 pt-1 border-t border-border/30 space-y-1">
          {slide.subtitle && (
            <p className="text-xs text-muted-foreground italic">{slide.subtitle}</p>
          )}
          {slide.bullets && slide.bullets.length > 0 && (
            <ul className="space-y-0.5">
              {slide.bullets.map((b, i) => {
                const { text, icon } = getBulletText(b)
                return (
                  <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                    <span className="mt-0.5">{icon || '•'}</span>
                    <span>{text}</span>
                  </li>
                )
              })}
            </ul>
          )}
          {slide.stats && (
            <div className="flex gap-2 flex-wrap">
              {slide.stats.map((s, i) => (
                <div key={i} className="bg-muted rounded-lg px-2 py-1">
                  {s.icon && <span className="text-sm">{s.icon}</span>}
                  <p className="text-sm font-emphasis text-orange-600">{s.value}</p>
                  <p className="text-[10px] text-muted-foreground">{s.label}</p>
                </div>
              ))}
            </div>
          )}
          {slide.quote && (
            <p className="text-xs text-muted-foreground italic">"{slide.quote}"</p>
          )}
          {slide.attribution && (
            <p className="text-[10px] text-muted-foreground">&mdash; {slide.attribution}</p>
          )}
          {slide.cta && (
            <p className="text-xs font-medium text-orange-600">CTA: {slide.cta}</p>
          )}
          {slide.left_header && slide.right_header && (
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <p className="font-medium text-foreground">{slide.left_header}</p>
                {slide.left_bullets && (
                  <ul className="mt-1 space-y-0.5">
                    {slide.left_bullets.map((b, i) => {
                      const { text, icon } = getBulletText(b)
                      return (
                        <li key={i} className="text-muted-foreground flex items-start gap-1">
                          <span>{icon || '•'}</span>
                          <span>{text}</span>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
              <div>
                <p className="font-medium text-foreground">{slide.right_header}</p>
                {slide.right_bullets && (
                  <ul className="mt-1 space-y-0.5">
                    {slide.right_bullets.map((b, i) => {
                      const { text, icon } = getBulletText(b)
                      return (
                        <li key={i} className="text-muted-foreground flex items-start gap-1">
                          <span>{icon || '•'}</span>
                          <span>{text}</span>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function humanizeKey(key: string): string {
  return key
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

// Agents typically return JSON, sometimes wrapped in ```json fences or with
// leading prose. Extract the JSON object/array body if present.
function tryParseAgentContent(raw: string): unknown | null {
  if (!raw) return null
  const trimmed = raw.trim()
  const fenced = trimmed.match(/```(?:json)?\s*([\s\S]*?)\s*```/i)
  const candidate = fenced ? fenced[1] : trimmed
  // Locate the outermost { } or [ ] block in the candidate.
  const firstBrace = candidate.search(/[{[]/)
  if (firstBrace === -1) return null
  const lastClose = Math.max(candidate.lastIndexOf('}'), candidate.lastIndexOf(']'))
  if (lastClose <= firstBrace) return null
  const slice = candidate.slice(firstBrace, lastClose + 1)
  try {
    return JSON.parse(slice)
  } catch {
    return null
  }
}

// A short scalar that should render as an inline value (chip).
function isShortScalar(v: unknown): boolean {
  if (v === null || v === undefined) return true
  if (typeof v === 'number' || typeof v === 'boolean') return true
  if (typeof v === 'string') return v.length <= 60 && !v.includes('\n')
  return false
}

const PrimitiveValue: React.FC<{ value: unknown; pill?: boolean }> = ({ value, pill }) => {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground/50 italic text-xs">—</span>
  }
  if (typeof value === 'boolean') {
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium ${
        value
          ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
          : 'bg-red-500/15 text-red-700 dark:text-red-400'
      }`}>
        {value ? 'Yes' : 'No'}
      </span>
    )
  }
  if (typeof value === 'number') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-emphasis bg-orange-500/15 text-orange-700 dark:text-orange-400 font-mono">{value}</span>
  }
  const s = String(value)
  if (pill) {
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-accent/40 text-foreground border border-border/40">{s}</span>
  }
  return <span className="text-xs text-foreground/90 whitespace-pre-wrap leading-relaxed">{s}</span>
}

const StructuredValue: React.FC<{ value: unknown; depth?: number }> = ({ value, depth = 0 }) => {
  if (value === null || value === undefined || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return <PrimitiveValue value={value} />
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-muted-foreground/50 italic text-xs">none</span>
    }
    // Array of short scalars → pill row.
    if (value.every(isShortScalar)) {
      return (
        <div className="flex flex-wrap gap-1.5">
          {value.map((v, i) => <PrimitiveValue key={i} value={v} pill />)}
        </div>
      )
    }
    // Array of long strings → tidy bulleted list.
    if (value.every((v) => typeof v === 'string')) {
      return (
        <ul className="space-y-1.5">
          {value.map((v, i) => (
            <li key={i} className="flex gap-2 text-xs text-foreground/90 leading-relaxed">
              <span className="flex-shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full bg-orange-500/15 text-orange-600 dark:text-orange-400 text-[9px] font-emphasis mt-0.5">{i + 1}</span>
              <span className="whitespace-pre-wrap">{String(v)}</span>
            </li>
          ))}
        </ul>
      )
    }
    // Array of objects/mixed → numbered cards.
    return (
      <div className="space-y-2">
        {value.map((v, i) => (
          <div key={i} className="rounded-lg bg-card/60 dark:bg-card/40 border border-border/50 px-3 py-2.5">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-md bg-orange-500/15 text-orange-700 dark:text-orange-400 text-[10px] font-emphasis">{i + 1}</span>
              <span className="text-[10px] font-emphasis text-muted-foreground uppercase tracking-wider">Item</span>
            </div>
            <StructuredValue value={v} depth={depth + 1} />
          </div>
        ))}
      </div>
    )
  }

  // Object.
  const entries = Object.entries(value as Record<string, unknown>)
  if (entries.length === 0) {
    return <span className="text-muted-foreground/50 italic text-xs">empty</span>
  }

  const Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) =>
    depth === 0 ? (
      <div className="space-y-2.5">{children}</div>
    ) : (
      <div className="space-y-2.5 rounded-lg bg-card/40 dark:bg-card/30 border border-border/40 px-3 py-2.5">{children}</div>
    )

  return (
    <Wrapper>
      {entries.map(([k, v]) => {
        const label = humanizeKey(k)
        // Compact layout for short scalar values: label and value on one row.
        if (isShortScalar(v)) {
          return (
            <div key={k} className="flex items-baseline gap-2 flex-wrap">
              <span className="text-[10px] font-emphasis text-muted-foreground uppercase tracking-wider min-w-[80px]">{label}</span>
              <span className="flex-1"><PrimitiveValue value={v} /></span>
            </div>
          )
        }
        return (
          <div key={k} className="space-y-1">
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-1 h-1 rounded-full bg-orange-500/70" />
              <span className="text-[10px] font-emphasis text-muted-foreground uppercase tracking-wider">{label}</span>
            </div>
            <div className="pl-2.5">
              <StructuredValue value={v} depth={depth + 1} />
            </div>
          </div>
        )
      })}
    </Wrapper>
  )
}

const DiscussionPanel: React.FC<{ discussion: DiscussionEntry[] }> = ({ discussion }) => {
  const [openIdx, setOpenIdx] = useState<number | null>(null)

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 mb-1">
        <ChatText className="w-3.5 h-3.5 text-orange-500" />
        <p className="text-xs font-emphasis text-foreground">Agent Discussion</p>
      </div>
      {discussion.map((entry, idx) => {
        const info = agentIcons[entry.role] || { emoji: '🤖', color: 'border-border bg-muted' }
        const isOpen = openIdx === idx
        const parsed = tryParseAgentContent(entry.content)
        return (
          <div key={idx} className={`border-l-2 rounded-r-xl ${info.color} overflow-hidden`}>
            <button
              type="button"
              onClick={() => setOpenIdx(isOpen ? null : idx)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left"
            >
              <span className="text-sm">{info.emoji}</span>
              <span className="text-xs font-emphasis text-foreground flex-1">{entry.role}</span>
              {isOpen ? <CaretDown className="w-3 h-3 text-muted-foreground" /> : <CaretRight className="w-3 h-3 text-muted-foreground" />}
            </button>
            {isOpen && (
              <div className="px-3 pb-3 pt-0">
                {parsed !== null ? (
                  <StructuredValue value={parsed} />
                ) : (
                  <p className="text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed">
                    {entry.content.length > 600 ? entry.content.slice(0, 600) + '...' : entry.content}
                  </p>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

const GenerateDeckConfig: React.FC<GenerateDeckConfigProps> = ({ data, updateField, upstreamEnrichmentFields = [] }) => {
  // AI Generate is the paid half of this node; Attach Direct (send a file the
  // user already uploaded) is Included on every tier, which is why the node
  // itself stays in the palette (backend: PARTIALLY_GATED_NODE_TYPES) and only
  // this mode is locked. Same verdict the backend reaches at activation,
  // launch and POST /presentations/generate.
  const deckGenLocked = usePlanFeatures().isLocked('deck_generation')
  const [activeTab, setActiveTab] = useState<'settings' | 'preview'>('settings')
  const [isGenerating, setIsGenerating] = useState(false)
  const [preview, setPreview] = useState<PreviewData | null>(null)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const [pipelineStep, setPipelineStep] = useState('')
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null)
  const [isRenderingPdf, setIsRenderingPdf] = useState(false)
  const [pptxSlideImages, setPptxSlideImages] = useState<string[] | null>(null)
  const [pptxSlideIndex, setPptxSlideIndex] = useState(0)
  const [isRenderingPptx, setIsRenderingPptx] = useState(false)
  const logoInputRef = useRef<HTMLInputElement>(null)
  // Id base for the three custom-colour swatches. They keep their own 10px
  // caption rather than a `Field` (whose label is always `labelClass`), so the
  // htmlFor pair is wired by hand instead.
  const colorUid = useId()
  // Settings-level logo/brand colors, fetched once so the node panel can
  // surface the two silent fallbacks (resolve_logo_path, "Auto from Brand")
  // instead of looking like empty/unset fields. Best-effort only -- if this
  // fails, the panel just shows today's copy (no hint) rather than erroring.
  const [accountBranding, setAccountBranding] = useState<{ logoUrl?: string; brandColors?: Record<string, string>; guidelinesSet?: boolean }>({})

  useEffect(() => {
    authApi.getAccountSettings()
      .then(res => setAccountBranding({
        logoUrl: res.data.company_logo_url,
        brandColors: res.data.brand_colors,
        guidelinesSet: res.data.brand_guidelines_text_set,
      }))
      .catch(() => {})
  }, [])

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => { if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl) }
  }, [pdfBlobUrl])

  // Clear rendered previews (PDF iframe blob, PPTX slide images) when preview
  // data changes (regeneration) — they're snapshots of the previous slide JSON.
  useEffect(() => {
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl)
      setPdfBlobUrl(null)
    }
    setPptxSlideImages(null)
    setPptxSlideIndex(0)
  }, [preview])

  // Merge stored config OVER the defaults (not `||`): an AI-generated or legacy
  // node can carry an empty/partial generate_deck_config ({}), which is truthy
  // and would bypass a `||` fallback, leaving every field undefined and crashing
  // the sub-selectors (material_ids, contact_fields, slide_count, ...).
  const config: import('@/types/workflow').GenerateDeckConfig = {
    mode: 'ai_generate' as CollateralMode,
    material_ids: [] as number[],
    contact_fields: ['job_title', 'company_name', 'company_industry'] as ContactContextField[],
    slide_count: 6,
    content_focus: 'pitch_deck' as DeckContentFocus,
    tone: 'professional' as EmailTone,
    custom_instructions: '',
    color_theme: 'auto_from_brand' as DeckColorTheme,
    custom_colors: undefined,
    logo_url: '',
    output_format: 'pdf' as import('@/types/workflow').DeckOutputFormat,
    attached_material_id: undefined,
    ...(data.generate_deck_config || {}),
  }

  const updateConfig = (key: string, value: any) => {
    updateField('generate_deck_config', { ...config, [key]: value })
  }

  // Auto-select enrichment fields from upstream EnrichContacts node.
  // useRef guard: runs at most once per mount. The sparse dep array is intentional —
  // do NOT add `config` or `updateConfig` to deps (would break the one-shot guarantee).
  const enrichmentApplied = useRef(false)
  useEffect(() => {
    if (enrichmentApplied.current) return
    if (!upstreamEnrichmentFields.length) return
    enrichmentApplied.current = true
    const current: ContactContextField[] = config.contact_fields || []
    const toAdd = upstreamEnrichmentFields.filter(f => !current.includes(f))
    if (toAdd.length > 0) {
      updateConfig('contact_fields', [...current, ...toAdd])
    }
  }, [upstreamEnrichmentFields])

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const allowed = ['image/png', 'image/jpeg', 'image/jpg']
    if (!allowed.includes(file.type)) {
      toast.error('Only PNG and JPG images are supported.')
      return
    }

    setIsUploadingLogo(true)
    try {
      const res = await presentationsApi.uploadLogo(file)
      updateConfig('logo_url', res.data.logo_url)
      toast.success('Logo uploaded!')
    } catch {
      toast.error('Failed to upload logo.')
    } finally {
      setIsUploadingLogo(false)
    }
  }

  const handleGeneratePreview = async () => {
    setIsGenerating(true)
    setPipelineStep('Queued')
    try {
      // Async-job pattern: backend returns job_id immediately, we poll
      // /presentations/job/{id} and surface server-reported progress.
      const res = await presentationsApi.previewAndPoll(
        {
          material_ids: config.material_ids,
          contact_fields: config.contact_fields as string[],
          slide_count: config.slide_count,
          content_focus: config.content_focus,
          tone: config.tone,
          custom_instructions: config.custom_instructions,
          color_theme: config.color_theme,
          custom_colors: config.custom_colors,
          logo_url: config.logo_url,
        },
        (msg) => setPipelineStep(msg),
      )

      setPreview(res.data)
      setActiveTab('preview')
    } catch (err: any) {
      toast.error(extractPresentationError(err, 'Failed to generate preview. Check your Anthropic API key in Settings.'))
    } finally {
      setIsGenerating(false)
      setPipelineStep('')
    }
  }

  // PDF-only: renders an inline iframe preview of the deck. Only meaningful
  // when output_format is 'pdf' — a PPTX file can't be shown in an iframe,
  // and rendering one here would just be a wasted conversion the user never
  // asked for. No LLM call either way (slides are already generated).
  const handleRenderPdfPreview = async () => {
    if (!preview) return
    setIsRenderingPdf(true)
    try {
      const res = await presentationsApi.renderSlides({
        title: preview.title,
        slides: preview.slides,
        color_theme: preview.color_theme,
        color_theme_key: config.color_theme,
        custom_colors: config.custom_colors,
        output_format: 'pdf',
        logo_url: config.logo_url,
      })
      if (res.data.download_url) {
        const blob = await presentationsApi.fetchFileBlob(res.data.download_url)
        if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl)
        setPdfBlobUrl(URL.createObjectURL(blob))
      }
    } catch (err: any) {
      toast.error(extractPresentationError(err, 'Failed to render PDF preview.'))
    } finally {
      setIsRenderingPdf(false)
    }
  }

  // PPTX-only: builds the actual PPTX and rasterizes it to WebP slide images
  // for an in-panel preview (PPTX has no native <iframe> preview like PDF
  // does). Still no LLM call — the slides already exist from Run Test; this
  // is just the same cheap file-build step as Download PPTX, plus a
  // LibreOffice conversion for the preview images.
  const handleRenderPptxPreview = async () => {
    if (!preview) return
    setIsRenderingPptx(true)
    try {
      const res = await presentationsApi.renderSlides({
        title: preview.title,
        slides: preview.slides,
        color_theme: preview.color_theme,
        color_theme_key: config.color_theme,
        custom_colors: config.custom_colors,
        output_format: 'pptx',
        return_preview: true,
        logo_url: config.logo_url,
      })
      setPptxSlideImages(res.data.preview_images || [])
      setPptxSlideIndex(0)
    } catch (err: any) {
      toast.error(extractPresentationError(err, 'Failed to render PPTX preview.'))
    } finally {
      setIsRenderingPptx(false)
    }
  }

  const handleGenerateFile = async (format: 'pdf' | 'pptx') => {
    if (!preview) {
      toast.error('Generate a preview first (Run Test), then download.')
      return
    }
    setIsGenerating(true)
    const formatLabel = format.toUpperCase()
    setPipelineStep(`Rendering ${formatLabel}...`)
    try {
      // The slides have already been produced by Run Test (/preview ran
      // Strategist → Copywriter → Designer). Downloading is just a file-
      // build step in the node's configured output_format — no LLM calls,
      // ~2–10 seconds, and never builds the other format.
      const res = await presentationsApi.renderSlides({
        title: preview.title,
        slides: preview.slides,
        color_theme: preview.color_theme,
        color_theme_key: config.color_theme,
        custom_colors: config.custom_colors,
        output_format: format,
        logo_url: config.logo_url,
      })

      if (res.data.download_url) {
        const blob = await presentationsApi.fetchFileBlob(res.data.download_url)
        const fallbackName = `presentation.${format}`
        const filename = res.data.filename || fallbackName
        const objectUrl = URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = objectUrl
        anchor.download = filename
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
        URL.revokeObjectURL(objectUrl)
        toast.success(`${formatLabel} ready. Download started.`)
      }
    } catch (err: any) {
      toast.error(extractPresentationError(err, `Failed to render ${formatLabel}.`))
    } finally {
      setIsGenerating(false)
      setPipelineStep('')
    }
  }

  return (
    <div className={sectionClass}>
      {/* Label */}
      <Field label="Label">
        {(f) => (
          <input
            {...f}
            type="text"
            value={data.label || ''}
            onChange={(e) => updateField('label', e.target.value)}
            placeholder="e.g., Sales Pitch Deck"
            className={inputClass} style={inputStyle}
          />
        )}
      </Field>

      {/* Mode selector */}
      <Field label="Mode" as="group">
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => updateConfig('mode', 'ai_generate')}
            disabled={deckGenLocked}
            title={deckGenLocked ? 'AI deck generation is not included in your plan' : undefined}
            className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all duration-150 ${
              config.mode !== 'attach_direct'
                ? 'border-orange-500 bg-orange-500/15'
                : 'border-border/50 hover:border-border'
            } ${deckGenLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            <Sparkle className="w-4 h-4 text-[#5e6ad2]" />
            <span className="text-xs font-emphasis text-foreground flex items-center gap-1">
              AI Generate
              {deckGenLocked && <LockSimple className="w-3 h-3 text-muted-foreground" aria-label="Requires a plan upgrade" />}
            </span>
            <span className="text-[9px] text-muted-foreground">
              {deckGenLocked ? 'Requires a plan upgrade' : 'Create with AI'}
            </span>
          </button>
          <button
            type="button"
            onClick={() => updateConfig('mode', 'attach_direct')}
            className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all duration-150 ${
              config.mode === 'attach_direct'
                ? 'border-orange-500 bg-orange-500/15'
                : 'border-border/50 hover:border-border'
            }`}
          >
            <Paperclip className="w-4 h-4 text-orange-500" />
            <span className="text-xs font-emphasis text-foreground">Attach Direct</span>
            <span className="text-[9px] text-muted-foreground">Use uploaded file</span>
          </button>
        </div>
      </Field>

      {deckGenLocked && config.mode !== 'attach_direct' && (
        <div className={dangerBannerClass}>
          <LockSimple className="w-4 h-4 mt-0.5 flex-shrink-0" weight="fill" />
          <p className="text-xs">
            AI deck generation is not included in your plan, so this workflow can't be activated
            while this step is set to AI Generate. Switch to Attach Direct to send a file you have
            already uploaded, or compare plans in Billing.
          </p>
        </div>
      )}

      {config.mode === 'attach_direct' ? (
        <AttachDirectPanel config={config} updateConfig={updateConfig} />
      ) : (
      <>
      {/* Model badge */}
      <div className={cn(infoBannerClass, 'items-center')}>
        <Sparkle className="w-4 h-4" />
        <div>
          <p className="text-xs font-emphasis">3-Agent Pipeline</p>
          <p className="text-[10px] opacity-80">Content Strategist &rarr; Copywriter &rarr; Design Director</p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-border/50">
        <button
          type="button"
          onClick={() => setActiveTab('settings')}
          className={`px-3 py-2 text-xs font-medium border-b-2 transition-all duration-150 ${
            activeTab === 'settings'
              ? 'border-orange-500 text-orange-600 dark:text-orange-400'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Settings
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('preview')}
          className={`px-3 py-2 text-xs font-medium border-b-2 transition-all duration-150 flex items-center gap-1 ${
            activeTab === 'preview'
              ? 'border-orange-500 text-orange-600 dark:text-orange-400'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Eye className="w-3 h-3" />
          Preview
          {preview && <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />}
        </button>
      </div>

      {activeTab === 'settings' ? (
        <div className={sectionClass}>
          {/* Material Selector */}
          <MaterialSelector
            selectedIds={config.material_ids}
            onChange={(ids) => updateConfig('material_ids', ids)}
            allowedContentTypes={ATTACHABLE_CONTENT_TYPES}
          />

          {/* Logo Upload */}
          <Field
            as="group"
            label={
              <span className="flex items-center gap-1.5">
                <Image className="w-3.5 h-3.5" />
                Logo
              </span>
            }
            hint="Appears on title, closing, and footer of all slides"
          >
            {config.logo_url ? (
              <div className="flex items-center gap-2 p-2 bg-muted rounded-xl border border-border/50">
                <img
                  src={`${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:8000'}${config.logo_url}`}
                  alt="Logo"
                  className="w-10 h-10 object-contain rounded"
                />
                <span className="text-xs text-muted-foreground flex-1 truncate">Logo uploaded</span>
                <button
                  type="button"
                  onClick={() => updateConfig('logo_url', '')}
                  className="p-1 hover:bg-muted rounded transition-all duration-150"
                >
                  <X className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              </div>
            ) : (
              <>
                {accountBranding.logoUrl && (
                  <div className="flex items-center gap-2 mb-2">
                    <img
                      src={`${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:8000'}${accountBranding.logoUrl}`}
                      alt="Account logo"
                      className="h-8 w-8 object-contain rounded border border-border/50 opacity-80"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-foreground truncate">Using your account logo</p>
                      <p className="text-[10px] text-muted-foreground">Upload one here to override, just for this deck</p>
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={isUploadingLogo}
                  className="w-full flex items-center justify-center gap-2 px-3 py-3 border-2 border-dashed border-border/60 rounded-xl hover:border-orange-400 dark:hover:border-orange-500 transition-all duration-150 text-xs text-muted-foreground"
                >
                  <UploadSimple className="w-4 h-4" />
                  {isUploadingLogo ? 'Uploading...' : 'Upload logo (PNG, JPG)'}
                </button>
              </>
            )}
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              className="hidden"
              onChange={handleLogoUpload}
            />
          </Field>

          {/* Contact Field Selector */}
          <ContactFieldSelector
            selectedFields={config.contact_fields as ContactContextField[]}
            onChange={(fields) => updateConfig('contact_fields', fields)}
            upstreamEnrichmentFields={upstreamEnrichmentFields}
          />

          {/* Slide Count */}
          <Field label="Number of Slides">
            {(f) => (
              <div className="flex items-center gap-3">
                <input
                  {...f}
                  type="range"
                  min={3}
                  max={15}
                  value={config.slide_count}
                  onChange={(e) => updateConfig('slide_count', parseInt(e.target.value))}
                  className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-orange-500"
                />
                <span className="text-sm font-emphasis text-orange-600 dark:text-orange-400 w-8 text-center">
                  {config.slide_count}
                </span>
              </div>
            )}
          </Field>

          {/* Content Focus */}
          <Field
            label="Content Focus"
            hint={contentFocusOptions.find(f => f.value === config.content_focus)?.description}
          >
            {(fld) => (
              <Select
                {...fld}
                value={config.content_focus}
                onChange={(value) => updateConfig('content_focus', value)}
                options={contentFocusOptions.map(f => ({ value: f.value, label: f.label }))}
                className="w-full"
              />
            )}
          </Field>

          {/* Tone */}
          <Field label="Tone">
            {(f) => (
              <Select
                {...f}
                value={config.tone}
                onChange={(value) => updateConfig('tone', value)}
                options={tones.map(t => ({ value: t.value, label: t.label }))}
                className="w-full"
              />
            )}
          </Field>

          {/* Color Theme */}
          <Field
            as="group"
            label={
              <span className="flex items-center gap-1.5">
                <Palette className="w-3.5 h-3.5" />
                Color Theme
              </span>
            }
          >
            <div className="grid grid-cols-4 gap-2">
              {colorThemes.map(theme => (
                <button
                  key={theme.value}
                  type="button"
                  onClick={() => {
                    // One atomic update, not two sequential updateConfig calls --
                    // each of those reads the same render-time `config` snapshot,
                    // so a second call's spread doesn't see the first call's
                    // change and clobbers it back to the old color_theme. That
                    // silently reverted every preset click (WI: color theme
                    // can't be switched from the UI).
                    updateField('generate_deck_config', {
                      ...config,
                      color_theme: theme.value,
                      ...(theme.value !== 'custom' ? { custom_colors: undefined } : {}),
                    })
                  }}
                  className={`relative flex flex-col items-center gap-1 p-2 rounded-xl border-2 transition-all duration-150 ${
                    config.color_theme === theme.value
                      ? 'border-orange-500 bg-orange-500/15'
                      : 'border-border/50 hover:border-border'
                  }`}
                  title={theme.description}
                >
                  {theme.value === 'auto_from_brand' ? (
                    <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                      <Sparkle className="w-3 h-3 text-white" />
                    </div>
                  ) : theme.value === 'custom' ? (
                    <div className="w-6 h-6 rounded-full flex items-center justify-center bg-gradient-to-br from-orange-400 via-fuchsia-500 to-indigo-500">
                      <Palette className="w-3 h-3 text-white" />
                    </div>
                  ) : (
                    <div className="flex -space-x-1">
                      <div className="w-4 h-4 rounded-full border border-white dark:border-gray-800" style={{ backgroundColor: theme.primary }} />
                      <div className="w-4 h-4 rounded-full border border-white dark:border-gray-800" style={{ backgroundColor: theme.accent }} />
                    </div>
                  )}
                  <span className="text-[9px] text-muted-foreground text-center leading-tight">
                    {theme.value === 'auto_from_brand' ? 'Auto' : theme.label.split(' ')[0]}
                  </span>
                </button>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1.5">
              {config.color_theme === 'auto_from_brand'
                ? (accountBranding.brandColors && Object.keys(accountBranding.brandColors).length > 0
                  ? 'Uses your saved brand colors'
                  : 'AI picks a monochromatic palette from brand context')
                : colorThemes.find(t => t.value === config.color_theme)?.description}
            </p>

            {/* Brand color swatches — read-only preview of the Settings-level
                colors "Auto from Brand" will use, same visual layout as the
                Custom color inputs below but with no <input>/onChange. */}
            {config.color_theme === 'auto_from_brand' && accountBranding.brandColors && Object.keys(accountBranding.brandColors).length > 0 && (
              <div className="mt-2 grid grid-cols-3 gap-2">
                {(['primary', 'accent', 'secondary'] as const)
                  .filter(key => accountBranding.brandColors?.[key])
                  .map(key => (
                    <div key={key}>
                      <span className="text-[10px] text-muted-foreground capitalize">{key}</span>
                      <div className="flex items-center gap-1 mt-0.5">
                        <div
                          className="w-6 h-6 rounded border border-border/50"
                          style={{ backgroundColor: accountBranding.brandColors![key] }}
                        />
                        <span className="text-[10px] text-muted-foreground font-mono">
                          {accountBranding.brandColors![key]}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {/* Custom color inputs */}
            {config.color_theme === 'custom' && (
              <div className="mt-2 grid grid-cols-3 gap-2">
                {(['primary', 'accent', 'secondary'] as const).map(key => (
                  <div key={key}>
                    <label htmlFor={`${colorUid}${key}`} className="text-[10px] text-muted-foreground capitalize">{key}</label>
                    <div className="flex items-center gap-1 mt-0.5">
                      <input
                        id={`${colorUid}${key}`}
                        type="color"
                        value={config.custom_colors?.[key] || (key === 'primary' ? '#1E40AF' : key === 'accent' ? '#3B82F6' : '#0F172A')}
                        onChange={(e) => {
                          const prev = config.custom_colors || { primary: '#1E40AF', accent: '#3B82F6', secondary: '#0F172A' }
                          updateConfig('custom_colors', { ...prev, [key]: e.target.value })
                        }}
                        className="w-6 h-6 rounded cursor-pointer border-0"
                      />
                      <span className="text-[10px] text-muted-foreground font-mono">
                        {config.custom_colors?.[key] || (key === 'primary' ? '#1E40AF' : key === 'accent' ? '#3B82F6' : '#0F172A')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Field>

          {/* Output Format */}
          <Field
            as="group"
            label={
              <span className="flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" />
                Output Format
              </span>
            }
          >
            <div className="grid grid-cols-2 gap-2">
              {([
                { value: 'pdf' as DeckOutputFormat, label: 'PDF', desc: 'HTML slides → PDF' },
                { value: 'pptx' as DeckOutputFormat, label: 'PPTX', desc: 'PptxGenJS → PowerPoint' },
              ]).map(fmt => (
                <button
                  key={fmt.value}
                  type="button"
                  onClick={() => updateConfig('output_format', fmt.value)}
                  className={`flex flex-col items-center gap-0.5 p-2 rounded-xl border-2 transition-all duration-150 ${
                    (config.output_format || 'pdf') === fmt.value
                      ? 'border-orange-500 bg-orange-500/15'
                      : 'border-border/50 hover:border-border'
                  }`}
                >
                  <span className="text-xs font-emphasis text-foreground">{fmt.label}</span>
                  <span className="text-[9px] text-muted-foreground">{fmt.desc}</span>
                </button>
              ))}
            </div>
          </Field>

          {/* Custom Instructions */}
          <Field label="Custom Instructions">
            {(f) => (
              <textarea
                {...f}
                value={config.custom_instructions}
                onChange={(e) => updateConfig('custom_instructions', e.target.value)}
                placeholder="E.g., Focus on ROI metrics, include competitive comparison, emphasize enterprise features..."
                rows={3}
                className={cn(textareaClass, 'h-[96px] overflow-y-auto')}
                style={inputStyle}
              />
            )}
          </Field>
          {accountBranding.guidelinesSet && (
            <p className="text-[10px] text-muted-foreground mt-1.5">
              Your uploaded brand book guidelines are also applied automatically.
            </p>
          )}

          {/* Run Test */}
          <Button
            variant="default"
            size="sm"
            className="w-full bg-orange-500 hover:bg-orange-600"
            onClick={handleGeneratePreview}
            loading={isGenerating}
            icon={<Play className="w-3.5 h-3.5" />}
          >
            {isGenerating ? pipelineStep || 'Running test...' : 'Run Test'}
          </Button>

          {isGenerating && (
            <div className={cn(infoBannerClass, 'items-center')}>
              <StackSimple className="w-3.5 h-3.5 animate-pulse" />
              <p className="text-xs">
                {pipelineStep || 'Crafting personalized presentation...'}
              </p>
            </div>
          )}
        </div>
      ) : (
        /* Preview Tab */
        <div className="space-y-3">
          {preview ? (
            <>
              {/* Deck title */}
              <div className={cn(infoBannerClass, 'block')}>
                <p className="block uppercase" style={sectionEyebrowStyle}>Presentation Title</p>
                <p className="text-sm font-emphasis text-foreground">{preview.title}</p>
                <p className="text-[10px] text-muted-foreground mt-1">{preview.slides.length} slides</p>
              </div>

              {/* Color theme swatch */}
              {preview.color_theme && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-lg">
                  <Palette className="w-3 h-3 text-muted-foreground" />
                  <span className="text-[10px] text-muted-foreground">Theme:</span>
                  <div className="flex -space-x-0.5">
                    {['primary', 'accent', 'secondary'].map(k => (
                      preview.color_theme?.[k] && (
                        <div
                          key={k}
                          className="w-3.5 h-3.5 rounded-full border border-white dark:border-gray-700"
                          style={{ backgroundColor: preview.color_theme[k] }}
                          title={`${k}: ${preview.color_theme[k]}`}
                        />
                      )
                    ))}
                  </div>
                </div>
              )}

              {/* Agent Discussion */}
              {preview.discussion && preview.discussion.length > 0 && (
                <DiscussionPanel discussion={preview.discussion} />
              )}

              {/* Slide preview — matches the node's configured output_format.
                  PDF renders inline as an iframe; PPTX has no native browser
                  preview, so it renders as WebP slide images instead (via
                  Render PPTX Preview). Neither path builds the other format. */}
              {(config.output_format || 'pdf') === 'pdf' ? (
                pdfBlobUrl ? (
                  <iframe
                    src={pdfBlobUrl}
                    className="w-full h-[300px] rounded-xl border border-border/50"
                    title="PDF Preview"
                  />
                ) : (
                  <div className="space-y-1.5">
                    {preview.slides.map((slide, idx) => (
                      <SlideCard key={idx} slide={slide} index={idx} />
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={handleRenderPdfPreview}
                      loading={isRenderingPdf}
                      icon={<FileText className="w-3.5 h-3.5" />}
                    >
                      {isRenderingPdf ? 'Rendering PDF...' : 'Render PDF Preview'}
                    </Button>
                  </div>
                )
              ) : pptxSlideImages && pptxSlideImages.length > 0 ? (
                <div>
                  <div className="flex items-center justify-center bg-gray-900 p-3 rounded-t-xl">
                    <img
                      src={pptxSlideImages[pptxSlideIndex]}
                      alt={`Slide ${pptxSlideIndex + 1}`}
                      className="w-full max-h-[300px] rounded-md shadow-lg object-contain"
                    />
                  </div>
                  <div className="flex items-center justify-between px-3 py-2 bg-muted rounded-b-xl border border-t-0 border-border/50">
                    <button
                      type="button"
                      onClick={() => setPptxSlideIndex(i => Math.max(0, i - 1))}
                      disabled={pptxSlideIndex === 0}
                      className="text-xs text-muted-foreground disabled:opacity-30"
                    >
                      &larr; Prev
                    </button>
                    <span className="text-[10px] text-muted-foreground">
                      Slide {pptxSlideIndex + 1} of {pptxSlideImages.length}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPptxSlideIndex(i => Math.min(pptxSlideImages.length - 1, i + 1))}
                      disabled={pptxSlideIndex === pptxSlideImages.length - 1}
                      className="text-xs text-muted-foreground disabled:opacity-30"
                    >
                      Next &rarr;
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {preview.slides.map((slide, idx) => (
                    <SlideCard key={idx} slide={slide} index={idx} />
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={handleRenderPptxPreview}
                    loading={isRenderingPptx}
                    icon={<FileText className="w-3.5 h-3.5" />}
                  >
                    {isRenderingPptx ? 'Rendering PPTX...' : 'Render PPTX Preview'}
                  </Button>
                </div>
              )}

              {/* Action buttons — only the format configured for this node */}
              <div className="space-y-2">
                <Button
                  variant="default"
                  size="sm"
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  onClick={() => handleGenerateFile(config.output_format || 'pdf')}
                  loading={isGenerating}
                  icon={<Download className="w-3.5 h-3.5" />}
                >
                  {isGenerating ? 'Generating...' : `Download ${(config.output_format || 'pdf').toUpperCase()}`}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={handleGeneratePreview}
                  loading={isGenerating}
                  icon={<ArrowsClockwise className="w-3.5 h-3.5" />}
                >
                  Regenerate
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-6">
              <StackSimple className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No preview yet</p>
              <p className="text-xs text-muted-foreground/70 mt-1">Configure settings and click Run Test</p>
            </div>
          )}
        </div>
      )}
      </>
      )}
    </div>
  )
}

interface AttachDirectPanelProps {
  config: import('@/types/workflow').GenerateDeckConfig
  updateConfig: (key: string, value: any) => void
}

interface MaterialItem {
  id: number
  title: string
  material_type: string
  content_type: string
  processing_status: string
  file_name?: string
}

const contentTypeLabels: Record<string, string> = {
  pdf: 'PDF', docx: 'DOC', pptx: 'PPT', website: 'WEB',
  text: 'TXT', excel: 'XLS', csv: 'CSV', markdown: 'MD',
}

// Content types that make sense for a deck node — either to attach directly
// or to feed into AI deck generation. Excludes crawled websites (no file,
// just a URL) which can't be sent as a deck.
const ATTACHABLE_CONTENT_TYPES = new Set([
  'pdf', 'docx', 'pptx', 'excel', 'csv', 'text', 'markdown',
])

const AttachDirectPanel: React.FC<AttachDirectPanelProps> = ({ config, updateConfig }) => {
  const [materials, setMaterials] = useState<MaterialItem[]>([])
  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setIsLoading(true)
    materialsApi.getAll()
      .then((res) => setMaterials(
        (res.data || []).filter(
          (m: MaterialItem) =>
            m.processing_status === 'completed' &&
            ATTACHABLE_CONTENT_TYPES.has(m.content_type)
        )
      ))
      .catch(() => setMaterials([]))
      .finally(() => setIsLoading(false))
  }, [])

  const filtered = materials.filter(m =>
    m.title.toLowerCase().includes(search.toLowerCase()) ||
    m.content_type.toLowerCase().includes(search.toLowerCase())
  )

  const selectedMaterial = materials.find(m => m.id === config.attached_material_id)

  return (
    <div className="space-y-3">
      <div className={cn(infoBannerClass, 'items-center')}>
        <Paperclip className="w-4 h-4" />
        <p className="text-xs">
          Attach an uploaded material directly — no AI generation.
        </p>
      </div>

      {selectedMaterial ? (
        <div className={cn(successBannerClass, 'items-center')}>
          <Check className="w-4 h-4" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{selectedMaterial.title}</p>
            <p className="text-[10px] text-muted-foreground">{contentTypeLabels[selectedMaterial.content_type] || selectedMaterial.content_type.toUpperCase()}</p>
          </div>
          <button
            type="button"
            onClick={() => updateConfig('attached_material_id', undefined)}
            className="p-1 hover:bg-emerald-500/15 rounded transition-all duration-150"
          >
            <X className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search materials..."
              aria-label="Search materials"
              className="w-full pl-9 pr-3 py-2 bg-card border border-border/60 rounded-lg text-sm text-foreground focus:border-orange-500/60 transition-all duration-150"
            />
          </div>

          <div className="max-h-60 overflow-y-auto border border-border/50 rounded-xl divide-y divide-border/30">
            {isLoading ? (
              <p className="text-xs text-muted-foreground text-center py-4">Loading materials...</p>
            ) : filtered.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">No materials found</p>
            ) : (
              filtered.map(m => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => updateConfig('attached_material_id', m.id)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-orange-500/10 transition-all duration-150"
                >
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-emphasis bg-muted text-muted-foreground">
                    {contentTypeLabels[m.content_type] || m.content_type.toUpperCase()}
                  </span>
                  <span className="text-sm text-foreground truncate flex-1">{m.title}</span>
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default GenerateDeckConfig
